import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getToken = () => localStorage.getItem("adminToken") || "";
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

export function CustomerFormModal({ customer, onClose, onSaved }) {
  const isEdit = !!customer;
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: customer?.name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
    billing_address: customer?.billing_address || "",
    shipping_address: customer?.shipping_address || "",
    notes_admin: customer?.notes_admin || "",
  });
  const [saving, setSaving] = useState(false);
  const [dupeMatches, setDupeMatches] = useState([]);

  const submit = async (e, force = false) => {
    e?.preventDefault?.();
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.email.trim() || !form.email.includes("@")) { toast.error("Valid email required"); return; }

    setSaving(true);
    try {
      const payload = { ...form, force };
      let res;
      if (isEdit) {
        res = await axios.patch(`${API}/admin/customers/${customer.customer_id}`, payload, { headers: headers() });
      } else {
        res = await axios.post(`${API}/admin/customers`, payload, { headers: headers() });
      }

      // Dupe detected — show warning, don't close
      if (res.data?.duplicate) {
        setDupeMatches(res.data.matches || []);
        setSaving(false);
        return;
      }

      toast.success(isEdit ? "Customer updated" : "Customer created");
      onSaved?.(res.data.customer);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    }
    setSaving(false);
  };

  const handleUseExisting = (match) => {
    onClose?.();
    navigate(`/admin/customers/${match.customer_id || match.id}`);
  };

  return (
    <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="customer-form-modal">
      <div className="bg-white max-w-lg w-full p-6 border border-[#282828]/8 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-[18px] text-[#282828]" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 600 }}>
            {isEdit ? "Edit Customer" : "Create Customer"}
          </h3>
          <button onClick={onClose} className="p-1 text-[#676664]/30 hover:text-[#282828]" data-testid="close-modal-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        {dupeMatches.length > 0 && (
          <div className="mb-5 bg-amber-50 border border-amber-200 p-4" data-testid="dupe-warning">
            <div className="flex items-start gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="text-[12px] text-amber-900">
                <div className="font-semibold mb-0.5">Possible duplicate{dupeMatches.length > 1 ? "s" : ""} found</div>
                <div className="text-[11px] text-amber-700">A customer with this email or phone already exists. You can open it, or create anyway.</div>
              </div>
            </div>
            <div className="space-y-1.5 mt-3">
              {dupeMatches.map((m) => (
                <button
                  key={m.customer_id || m.id}
                  onClick={() => handleUseExisting(m)}
                  className="w-full text-left bg-white border border-amber-200 px-3 py-2 hover:bg-amber-100/30 flex items-center justify-between"
                  data-testid={`dupe-match-${m.customer_id || m.id}`}
                >
                  <div>
                    <div className="text-[12px] text-[#282828]">{m.name || "(no name)"} <span className="text-[#676664]/60">— {m.email}</span></div>
                    {m.phone && <div className="text-[10px] text-[#676664]/60">{m.phone}</div>}
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 text-amber-600" />
                </button>
              ))}
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={(e) => submit(e, true)} disabled={saving} className="text-[10px] text-amber-900 border border-amber-300 px-3 py-1.5 hover:bg-amber-100 uppercase tracking-wider" data-testid="create-anyway-btn">
                {saving ? "Saving…" : (isEdit ? "Save Anyway" : "Create Anyway")}
              </button>
              <button onClick={() => setDupeMatches([])} className="text-[10px] text-[#676664]/60 hover:text-[#282828] px-3 py-1.5">Dismiss</button>
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3">
          <Field label="Full Name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="customer-name-input" />
          <Field label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="customer-email-input" />
          <Field label="Phone (AU)" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="customer-phone-input" placeholder="04xx xxx xxx or +61 4xx xxx xxx" />
          <Field label="Billing Address" value={form.billing_address} onChange={(v) => setForm({ ...form, billing_address: v })} testid="customer-billing-input" />
          <Field label="Shipping Address" value={form.shipping_address} onChange={(v) => setForm({ ...form, shipping_address: v })} testid="customer-shipping-input" />
          <div>
            <label className="block text-[11px] uppercase tracking-wider text-[#676664]/60 mb-1">Internal Admin Notes</label>
            <textarea
              value={form.notes_admin}
              onChange={(e) => setForm({ ...form, notes_admin: e.target.value })}
              rows={2}
              className="w-full border border-[#282828]/12 px-3 py-2 text-[12px] focus:border-[#9A7B4F] focus:outline-none resize-none"
              placeholder="Not shown to customer."
              data-testid="customer-notes-input"
            />
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="border-[#282828]/10" data-testid="cancel-customer-btn">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-[#9A7B4F] hover:bg-[#8B6B3F] text-white" data-testid="save-customer-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Save Changes" : "Create Customer")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, testid }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider text-[#676664]/60 mb-1">{label}</label>
      <Input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="border-[#282828]/12 text-[13px]"
        data-testid={testid}
      />
    </div>
  );
}
